import type {
  OrderRequest,
  OrderResponse,
  PromoCodeDiscountedTicket,
  PromoCodeRequest,
  PromoCodeResponse,
  PromoCodeTicketRequest,
  TicketDatePrice,
  TicketReturnResponse,
  TicketType,
  TicketWidgetOptions,
} from "@/types";

export interface TicketWidgetClient {
  getTicketPrices: (
    dateFrom: string,
    dateTo: string,
    promoCode?: string,
  ) => Promise<TicketDatePrice[]>;
  getTicketTypes: (date: string, promoCode?: string) => Promise<TicketType[]>;
  getOrderTickets: (orderId: string) => Promise<OrderResponse>;
  checkPromoCode: (request: PromoCodeRequest) => Promise<PromoCodeResponse>;
  createOrder: (request: OrderRequest) => Promise<OrderResponse>;
  cancelTicket: (ticketId: string) => Promise<TicketReturnResponse>;
}

interface ApiResponse {
  success: boolean;
  message?: string;
}

interface TicketPricesResponse extends ApiResponse {
  tickets: Array<{
    date: string;
    min_price: number | string;
    currency: string;
  }>;
}

interface TicketsResponse extends ApiResponse {
  tickets: Array<{
    ticket_type_no: number | string;
    ticket_type_name?: string;
    person_type_no: number | string;
    person_type_name?: string;
    pool_no: number | string;
    pool_name?: string;
    project_no?: number | string;
    currency: string;
    amount?: number | string;
    original_price?: number | string | null;
    promo_code?: string | null;
    tariff_description?: string;
    date?: string;
    price?: number | string;
    name?: string;
    description?: string;
  }>;
}

interface OrderApiResponse extends ApiResponse {
  order_id: string;
  payment_url?: string | null;
  tickets: OrderTicketApiResult[];
}

interface OrderTicketApiResult {
  id: string;
  order_id?: string;
  order_number?: number | string;
  ticket_type_no: number | string;
  ticket_type_name?: string;
  person_type_no: number | string;
  person_type_name?: string;
  pool_no: number | string;
  pool_name?: string;
  project_no?: number | string;
  valid_from?: string;
  date?: string;
  quantity?: number | string;
  price: number | string;
  barcode: string;
  barcode_image?: string;
  status: string;
  is_confirmed?: number | string | boolean | null;
  serial_no?: number | string;
  journal_no?: number | string;
  pos_no?: number | string;
  trans_no?: number | string;
  download_url?: string;
  name?: string;
  title?: string;
  subtitle?: string;
}

interface OrderTicketsApiResponse extends ApiResponse {
  order?: {
    id: string;
    status?: string;
    amount?: number | string;
    last_name?: string;
    first_name?: string;
    email?: string;
    phone?: string;
    promo_code?: string | null;
    trans_no?: number | string;
    created_at?: string;
  };
  order_id?: string;
  tickets: OrderTicketApiResult[];
}

interface CancelTicketResponse extends ApiResponse {}

interface PromoCodeTicketApiResult {
  person_type_no?: number | string;
  pool_no?: number | string;
  ticket_type_no?: number | string;
  valid_from?: string;
  discount_abs?: number | string;
  discount_percent?: number | string;
  original_price?: number | string;
  price_discounted?: number | string;
  error_no?: number | string;
  error_message?: string;
}

interface PromoCodeApiResponse extends ApiResponse {
  return_code?: number | string;
  discounted_tickets?: PromoCodeTicketApiResult[];
  promo_code_result?: PromoCodeTicketApiResult | null;
  promo_code_results?: PromoCodeTicketApiResult[];
}

interface ApiDataResponse<T> {
  data: T;
}

export function createTicketWidgetClient(
  options: TicketWidgetOptions,
): TicketWidgetClient {
  return {
    getTicketPrices: (dateFrom, dateTo, promoCode) =>
      getTicketPrices(options, dateFrom, dateTo, promoCode),
    getTicketTypes: (date, promoCode) =>
      getTicketTypes(options, date, promoCode),
    getOrderTickets: (orderId) => getOrderTickets(options, orderId),
    checkPromoCode: (payload) => checkPromoCode(options, payload),
    createOrder: (payload) => createOrder(options, payload),
    cancelTicket: (ticketId) => cancelTicket(options, ticketId),
  };
}

async function getOrderTickets(
  options: TicketWidgetOptions,
  orderId: string,
): Promise<OrderResponse> {
  const apiResponse = await request<
    OrderTicketsApiResponse | ApiDataResponse<OrderTicketsApiResponse>
  >(
    options,
    `/api/orders/${encodeURIComponent(orderId)}/tickets`,
  );
  const response = unwrapDataResponse(apiResponse);

  assertApiSuccess(response, "Не удалось загрузить билеты заказа.");

  const resolvedOrderId = response.order?.id ?? response.order_id ?? orderId;

  return {
    id: resolvedOrderId,
    status: response.order?.status ?? "created",
    amount:
      response.order?.amount === undefined
        ? undefined
        : toNumber(response.order.amount),
    email: response.order?.email,
    lastName: response.order?.last_name,
    firstName: response.order?.first_name,
    phone: response.order?.phone,
    promoCode: response.order?.promo_code,
    transNo:
      response.order?.trans_no === undefined
        ? undefined
        : String(response.order.trans_no),
    createdAt: response.order?.created_at,
    tickets: response.tickets.map((ticket) =>
      normalizeOrderTicket(ticket, resolvedOrderId),
    ),
  };
}

async function checkPromoCode(
  options: TicketWidgetOptions,
  payload: PromoCodeRequest,
): Promise<PromoCodeResponse> {
  const apiResponse = await request<
    PromoCodeApiResponse | ApiDataResponse<PromoCodeApiResponse>
  >(options, "/api/tickets/check-promo-code", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const response = unwrapDataResponse(apiResponse);

  assertApiSuccess(response, "Промокод не найден.");

  const discountedTickets = normalizePromoCodeTickets(response, payload.tickets);
  const discountedTotalPrice = discountedTickets.reduce(
    (sum, ticket) => sum + ticket.discountedPrice,
    0,
  );

  return {
    code: payload.promo_code,
    discountAmount: discountedTickets.reduce(
      (sum, ticket) => sum + ticket.discountAmount,
      0,
    ),
    discountedTotalPrice,
    discountedTickets,
    message: response.message,
  };
}

async function cancelTicket(
  options: TicketWidgetOptions,
  ticketId: string,
): Promise<TicketReturnResponse> {
  const response = await request<CancelTicketResponse>(
    options,
    `/api/tickets/${encodeURIComponent(ticketId)}/cancel`,
    {
      method: "POST",
    },
  );

  assertApiSuccess(response, "Не удалось вернуть билет.");

  return {
    message: response.message || "Билет успешно возвращён",
  };
}

async function createOrder(
  options: TicketWidgetOptions,
  payload: OrderRequest,
): Promise<OrderResponse> {
  const response = await request<OrderApiResponse>(options, "/api/tickets/order", {
    method: "POST",
    body: JSON.stringify(payload),
  });

  assertApiSuccess(response, "Не удалось создать заказ.");

  return {
    id: response.order_id,
    status: response.payment_url ? "pending_payment" : "created",
    paymentUrl: response.payment_url ?? undefined,
    message: response.message,
    tickets: response.tickets.map((ticket) =>
      normalizeOrderTicket(ticket, response.order_id),
    ),
  };
}

async function getTicketPrices(
  options: TicketWidgetOptions,
  dateFrom: string,
  dateTo: string,
  promoCode?: string,
): Promise<TicketDatePrice[]> {
  const query = createQuery({
    date_from: dateFrom,
    date_to: dateTo,
    promo_code: promoCode,
  });

  const response = await request<TicketPricesResponse>(
    options,
    `/api/tickets/prices?${query}`,
  );

  assertApiSuccess(response, "Не удалось получить цены на билеты.");

  return response.tickets.map((ticket) => ({
    date: ticket.date,
    minPrice: toNumber(ticket.min_price),
    currency: ticket.currency,
  }));
}

async function getTicketTypes(
  options: TicketWidgetOptions,
  date: string,
  promoCode?: string,
): Promise<TicketType[]> {
  const query = createQuery({
    date,
    promo_code: promoCode,
  });

  const response = await request<TicketsResponse>(
    options,
    `/api/tickets?${query}`,
  );

  assertApiSuccess(response, "Не удалось получить билеты на выбранную дату.");

  return response.tickets.map((ticket) => {
    const ticketTypeNo = toNumber(ticket.ticket_type_no);
    const personTypeNo = toNumber(ticket.person_type_no);
    const poolNo = toNumber(ticket.pool_no);
    const ticketDate = ticket.date ?? date;
    const title = ticket.name ?? ticket.ticket_type_name ?? "Билет";

    return {
      id: [ticketTypeNo, personTypeNo, poolNo, ticketDate].join("_"),
      title,
      description: ticket.description ?? ticket.tariff_description ?? title,
      price: toNumber(ticket.price ?? ticket.amount ?? 0),
      ticketTypeNo,
      personTypeNo,
      poolNo,
      date: ticketDate,
      currency: ticket.currency,
    };
  });
}

async function request<T>(
  options: TicketWidgetOptions,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const baseUrl = options.apiBaseUrl?.replace(/\/$/, "");

  if (!baseUrl) {
    throw new Error("TicketWidget: apiBaseUrl is required.");
  }

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: options.credentials ?? "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...options.headers,
      ...(options.accessToken
        ? { Authorization: `Bearer ${options.accessToken}` }
        : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response));
  }

  return response.json() as Promise<T>;
}

function createQuery(params: Record<string, string | undefined>) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      query.set(key, value);
    }
  });

  return query.toString();
}

function assertApiSuccess(response: ApiResponse, fallbackMessage: string) {
  if (!response.success) {
    throw new Error(response.message || fallbackMessage);
  }
}

function unwrapDataResponse<T>(response: T | ApiDataResponse<T>) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    (response as ApiDataResponse<T>).data
  ) {
    return (response as ApiDataResponse<T>).data;
  }

  return response as T;
}

function normalizeOrderTicket(
  ticket: OrderTicketApiResult,
  fallbackOrderId: string,
) {
  return {
    id: ticket.id,
    orderId: ticket.order_id ?? fallbackOrderId,
    orderNumber:
      ticket.order_number === undefined ? "" : String(ticket.order_number),
    title: ticket.title ?? ticket.name ?? ticket.ticket_type_name,
    subtitle: ticket.subtitle,
    ticketTypeNo: toNumber(ticket.ticket_type_no),
    personTypeNo: toNumber(ticket.person_type_no),
    poolNo: toNumber(ticket.pool_no),
    projectNo: toNumber(ticket.project_no),
    validFrom: ticket.valid_from ?? ticket.date ?? "",
    quantity: toNumber(ticket.quantity ?? 1),
    price: toNumber(ticket.price),
    barcode: ticket.barcode,
    barcodeImage: ticket.barcode_image ?? "",
    status: ticket.status,
    isConfirmed: normalizeBooleanFlag(ticket.is_confirmed),
    serialNo: ticket.serial_no ?? "",
    journalNo: ticket.journal_no ?? "",
    posNo: ticket.pos_no ?? "",
    transNo: ticket.trans_no ?? "",
    downloadUrl: ticket.download_url ?? "",
  };
}

function normalizeBooleanFlag(value: number | string | boolean | null | undefined) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  return value === "1";
}

function normalizePromoCodeTickets(
  response: PromoCodeApiResponse,
  requestedTickets: PromoCodeTicketRequest[],
): PromoCodeDiscountedTicket[] {
  if (response.discounted_tickets?.length) {
    return response.discounted_tickets.map((ticket) =>
      normalizePromoCodeTicket(ticket),
    );
  }

  if (response.promo_code_results?.length) {
    return response.promo_code_results.map((ticket, index) =>
      normalizePromoCodeTicket(ticket, requestedTickets[index]),
    );
  }

  if (response.promo_code_result) {
    return [
      normalizePromoCodeTicket(response.promo_code_result, requestedTickets[0]),
    ];
  }

  return [];
}

function normalizePromoCodeTicket(
  ticket: PromoCodeTicketApiResult,
  requestedTicket?: PromoCodeTicketRequest,
): PromoCodeDiscountedTicket {
  const originalPrice = toNumber(ticket.original_price);
  const apiDiscountAmount = toNumber(ticket.discount_abs);
  const discountedPrice =
    ticket.price_discounted === undefined || ticket.price_discounted === null
      ? Math.max(originalPrice - apiDiscountAmount, 0)
      : toNumber(ticket.price_discounted);
  const priceDiscountAmount =
    originalPrice > 0 && discountedPrice > 0
      ? Math.max(originalPrice - discountedPrice, 0)
      : 0;
  const discountAmount = Math.max(apiDiscountAmount, priceDiscountAmount);
  const errorNo =
    ticket.error_no === undefined ? undefined : toNumber(ticket.error_no);

  return {
    personTypeNo: toNumber(
      ticket.person_type_no ?? requestedTicket?.person_type_no,
    ),
    poolNo: toNumber(ticket.pool_no ?? requestedTicket?.pool_no),
    ticketTypeNo: toNumber(
      ticket.ticket_type_no ?? requestedTicket?.ticket_type_no,
    ),
    validFrom: ticket.valid_from ?? requestedTicket?.valid_from ?? "",
    discountAmount,
    discountPercent: toNumber(ticket.discount_percent),
    originalPrice,
    discountedPrice,
    errorNo,
    errorMessage: ticket.error_message,
  };
}

function toNumber(value: number | string | null | undefined) {
  return Number(value) || 0;
}

async function getErrorMessage(response: Response) {
  const fallbackMessage = `TicketWidget API error: ${response.status}`;
  const text = await response.text();

  try {
    const payload = JSON.parse(text) as { message?: string };
    return payload.message || fallbackMessage;
  } catch {
    return text || fallbackMessage;
  }
}
